import {
  MANUS_AUTOMATION_EXPORT_MAX_TRANSIENT_FAILURES,
  MANUS_AUTOMATION_EXPORT_POLL_INTERVAL_MS,
  MANUS_AUTOMATION_EXPORT_TIMEOUT_MS,
  MANUS_AUTOMATION_IMPORT_MAX_PER_REQUEST,
} from "../constants/manus-automation-import";
import { MANUS_IMPORT_MAX_AGENTS_PER_REQUEST } from "../constants/manus-import";
import type {
  IGetManusAutomationExportPayload,
  IImportManusAgentsPayload,
  IImportManusAutomationsPayload,
  IListManusAgentsPayload,
  IManusAgent,
  IManusAgentImportResult,
  IManusAutomationExport,
  IManusAutomationExportStart,
  IManusAutomationImportResult,
  IManusAutomationsRead,
  IReadManusAutomationsPayload,
  IStartManusAutomationExportPayload,
} from "../types";
import { EManusAutomationExportStatus } from "../types/manus-automation-import";
import { isTransientManusExportError } from "../utils/manus-automation-import";
import type { ApiClient } from "./client";
import { importInBatches, importItemsInBatches } from "./import-in-batches";

// NOTE: NSAI-8009 — thrown when an export is still running after the client
// stopped waiting. It carries the export id so the next attempt resumes the
// same Manus task instead of starting another one.
export class ManusAutomationExportTimeoutError extends Error {
  readonly exportId: string;

  constructor(exportId: string) {
    super("The Manus automation export is still running.");
    this.name = "ManusAutomationExportTimeoutError";
    this.exportId = exportId;
  }
}

interface IWaitProps {
  ms: number;
  signal?: AbortSignal | undefined;
}

const createAbortError = (): Error => {
  const error = new Error("The Manus automation export was cancelled.");
  error.name = "AbortError";
  return error;
};

// NOTE: setTimeout is all the lib can rely on (isomorphic: Vite and Metro).
const wait = ({ ms, signal }: IWaitProps): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(createAbortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

export class ManusImportApi {
  constructor(private readonly apiClient: ApiClient) {}

  async listAgents(data: IListManusAgentsPayload): Promise<IManusAgent[]> {
    const response = await this.apiClient.post<IManusAgent[]>(
      "/integrations/manus/agents",
      data,
    );
    return response.data;
  }

  // NOTE: Batched like every agent import — see importInBatches.
  async importAgents({
    apiKey,
    agents,
  }: IImportManusAgentsPayload): Promise<IManusAgentImportResult[]> {
    return importInBatches({
      agents,
      batchSize: MANUS_IMPORT_MAX_AGENTS_PER_REQUEST,
      sendBatch: async (batch) => {
        const response = await this.apiClient.post<IManusAgentImportResult[]>(
          "/integrations/manus/agents/import",
          { apiKey, agents: batch },
        );
        return response.data;
      },
    });
  }

  async startAutomationExport({
    signal,
    ...body
  }: IStartManusAutomationExportPayload): Promise<IManusAutomationExportStart> {
    const response = await this.apiClient.post<IManusAutomationExportStart>(
      "/integrations/manus/automations/export",
      body,
      { signal },
    );
    return response.data;
  }

  async getAutomationExport({
    signal,
    ...body
  }: IGetManusAutomationExportPayload): Promise<IManusAutomationExport> {
    const response = await this.apiClient.post<IManusAutomationExport>(
      "/integrations/manus/automations/export/status",
      body,
      { signal },
    );
    return response.data;
  }

  // NOTE: NSAI-8009 — the whole read the API key step waits on: start the
  // export (or resume `exportId`), then poll until Manus has finished, riding
  // out a few Manus hiccups in a row. Lives here so web and mobile poll on the
  // same cadence and give up at the same point, with
  // ManusAutomationExportTimeoutError.
  async readAutomations({
    apiKey,
    exportId,
    signal,
    onExportStarted,
  }: IReadManusAutomationsPayload): Promise<IManusAutomationsRead> {
    const activeExportId =
      exportId ??
      (await this.startAutomationExport({ apiKey, signal })).exportId;
    onExportStarted?.(activeExportId);
    const startedAtMs = Date.now();
    let transientFailures = 0;

    for (;;) {
      if (signal?.aborted) {
        throw createAbortError();
      }
      let state: IManusAutomationExport | null = null;
      try {
        state = await this.getAutomationExport({
          apiKey,
          exportId: activeExportId,
          signal,
        });
        transientFailures = 0;
      } catch (error) {
        // NOTE: A request the caller cancelled is an abort, not a Manus
        // hiccup to ride out.
        if (signal?.aborted) {
          throw createAbortError();
        }
        transientFailures += 1;
        if (
          !isTransientManusExportError(error) ||
          transientFailures > MANUS_AUTOMATION_EXPORT_MAX_TRANSIENT_FAILURES
        ) {
          throw error;
        }
      }
      // NOTE: An answer that lands after the caller gave up is not reported
      // as a result.
      if (signal?.aborted) {
        throw createAbortError();
      }
      if (state?.status === EManusAutomationExportStatus.COMPLETED) {
        return {
          exportId: activeExportId,
          automations: state.automations ?? [],
        };
      }
      if (Date.now() - startedAtMs >= MANUS_AUTOMATION_EXPORT_TIMEOUT_MS) {
        throw new ManusAutomationExportTimeoutError(activeExportId);
      }
      await wait({ ms: MANUS_AUTOMATION_EXPORT_POLL_INTERVAL_MS, signal });
    }
  }

  // NOTE: Batched like every import — see importItemsInBatches.
  async importAutomations({
    apiKey,
    exportId,
    automations,
  }: IImportManusAutomationsPayload): Promise<IManusAutomationImportResult[]> {
    return importItemsInBatches({
      items: automations,
      batchSize: MANUS_AUTOMATION_IMPORT_MAX_PER_REQUEST,
      sendBatch: async (batch) => {
        const response = await this.apiClient.post<
          IManusAutomationImportResult[]
        >("/integrations/manus/automations/import", {
          apiKey,
          exportId,
          automations: batch,
        });
        return response.data;
      },
      toUnconfirmedResult: (automation) => ({
        automationId: automation.automationId,
        success: false,
        isOutcomeUnknown: true,
      }),
    });
  }
}
